import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
    MDBBtn, MDBIcon, MDBRow, MDBCol, MDBTextArea, MDBCard,
    MDBCheckbox, MDBAlert, MDBSelect, MDBModal, MDBModalDialog,
    MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter
} from 'mdb-react-ui-kit';
import LeftPane from "../components/LeftPane";
import Header from "../components/Header";
import { getWhatsAppTEMP, updateSMSTemp, getappvariables, GetAPIsms, getWhatsappApi } from "../services/apiServices";
import EmailTempletes from './EmailTempletes';
import { useAnalytics } from "../hooks/useAnalytics";


// ─── helpers ──────────────────────────────────────────────────────────────────

/**
 * Normalise status for comparison — DB stores lowercase 'approved',
 * Meta API returns uppercase 'APPROVED'. Accept both.
 */
const isApproved = (t) =>
    t.status?.toLowerCase() === 'approved';

/**
 * Build flat variable mapping rows from a template object.
 *
 * Meta DB shape:  customParams: [{ paramName, location, type, index, urlTemplate?, example }]
 *   - type 'image'  → header_image_var  (dynamic image URL per member)
 *   - type 'text'   → header_var_N or body_var_N
 * WATI shape:     customParams: [{ paramName, example }]
 */
const buildVariableList = (templateData, provider) => {
    if (!templateData?.customParams?.length) return [];

    return templateData.customParams.map(p => ({
        whatsappvariable:    p.paramName,          // 'header_image_var' | 'header_var_1' | 'body_var_1'
        location:            p.location  || 'body',
        type:                p.type      || 'text', // 'image' | 'text'
        index:               p.index     || 1,
        urlTemplate:         p.urlTemplate || '',   // only set for type='image'
        applicationvariable: ''
    }));
};

/**
 * Human-readable label for a mapping row.
 * Meta image  → "Header Image URL"
 * Meta text   → "Header {{1}}" / "Body {{2}}"
 * WATI        → raw paramName
 */
const getVariableLabel = (mapping, provider) => {
    if (provider !== 'meta') return mapping.whatsappvariable;
    if (mapping.type === 'image') return 'Header Image URL';
    const loc = mapping.location === 'header' ? 'Header' : 'Body';
    return `${loc} {{${mapping.index}}}`;
};

/**
 * Text shown in the read-only textarea preview.
 * Meta: assembles header text + body + footer from components.
 * WATI: bodyOriginal ?? body.
 */
const getTemplateDisplayText = (templateData, provider) => {
    if (!templateData) return '';

    if (provider === 'meta' && templateData.components) {
        const { header, body, footer } = templateData.components;
        const parts = [];
        if (header?.format === 'TEXT' && header.text)   parts.push(`[Header]\n${header.text}`);
        if (header?.format === 'IMAGE')                  parts.push(`[Header: Image]`);
        if (body?.text)                                  parts.push(`[Body]\n${body.text}`);
        if (footer?.text)                                parts.push(`[Footer]\n${footer.text}`);
        return parts.join('\n\n');
    }

    return templateData.bodyOriginal ?? templateData.body ?? '';
};


// ─────────────────────────────────────────────────────────────────────────────

const EditMessage = () => {
    const location             = useLocation();
    const { trackButtonClick } = useAnalytics();
    const navigate             = useNavigate();
    const textAreaRef          = useRef(null);

    let plansInfo = localStorage.getItem("planInfo")
        ? JSON.parse(localStorage.getItem("planInfo")) : null;
    let gymid = JSON.parse(localStorage.getItem("loggedUserInfo"))?.gymid ?? "";

    // ── state ────────────────────────────────────────────────────────────────
    const [apiError,         setApiError]         = useState(false);
    const [form,             setForm]             = useState({});
    const [templateinfo,     setTemplateinfo]     = useState([]);
    const [selectData,       setSelectData]       = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [templateData,     setTemplateData]     = useState({});
    const [appVariables,     setAppvariables]     = useState([]);
    const [variableMappings, setVariableMappings] = useState([]);
    const [mappingErrors,    setMappingErrors]    = useState({});
    const [smsapi,           setSmsapi]           = useState([]);
    const [Alerttxt,         setAlerttxt]         = useState("");
    const [provider,         setProvider]         = useState("wati");

    // SMS
    const [smstemvariables,  setSmstemvariables]  = useState([]);
    const [smsvableMaping,   setSmsvarbleMaping]  = useState([]);

    // Preview
    const [prevCheck,        setPrevCheck]        = useState(false);
    const [previewTemplate,  setPreviewTemplate]  = useState("");
    const [inputValues,      setInputValues]      = useState({});
    const [preview,          setPreview]          = useState("");

    // ── init from route state ────────────────────────────────────────────────
    useEffect(() => {
        if (location?.state?.row) {
            setForm(location.state.row);
            if (location.state.row.wtstemplatename) {
                setSelectedTemplate(location.state.row.wtstemplatename);
                setVariableMappings(location.state.row.wtsvariablemaping || []);
            }
        }
    }, []);

    useEffect(() => {
        fetchData();
        fetchWhatsapiData();
    }, []);

    // ── build select options ─────────────────────────────────────────────────
    useEffect(() => {
        if (templateinfo?.length > 0) {
            const opts = templateinfo.map(p => ({ text: p.elementName, value: p.elementName }));
            setSelectData([{ text: "select a template", value: "" }, ...opts]);
        } else {
            setSelectData([{ text: "select a template", value: "" }]);
        }
    }, [templateinfo]);

    // ── rebuild variable mappings when templateData changes ──────────────────
    useEffect(() => {
        if (!templateData?.customParams?.length) {
            setVariableMappings([]);
            return;
        }

        const freshVars = buildVariableList(templateData, provider);
        const savedName = location.state?.row?.wtstemplatename;

        if (savedName === selectedTemplate) {
            // Restore saved applicationvariable values by matching on paramName
            const saved    = location.state.row.wtsvariablemaping || [];
            const savedMap = new Map(saved.map(m => [m.whatsappvariable, m.applicationvariable]));
            setVariableMappings(freshVars.map(v => ({
                ...v,
                applicationvariable: savedMap.get(v.whatsappvariable) || ''
            })));
        } else {
            setVariableMappings(freshVars);
        }
    }, [templateData]);

    // ── SMS variable extraction ───────────────────────────────────────────────
    useEffect(() => {
        setSmstemvariables(extractSmsVariables(form.templete || ''));
    }, [form.templete]);

    useEffect(() => {
        if (smstemvariables.length > 0) {
            if (location.state?.row?.templete === form.templete) {
                setSmsvarbleMaping(location.state.row.smsvariableMaping || []);
            } else {
                setSmsvarbleMaping(prev => {
                    const existing = new Map(prev.map(m => [m.smsvariable, m]));
                    return smstemvariables.map(v =>
                        existing.get(v) || { smsvariable: v, applicationvariable: '' }
                    );
                });
            }
        } else {
            setSmsvarbleMaping([]);
        }
    }, [smstemvariables]);

    // ── data fetching ─────────────────────────────────────────────────────────
    const fetchWhatsapiData = async () => {
        const response = await getWhatsappApi(gymid);
        if (!response || response === "false") return;
        await response.json();
    };

    const fetchData = async () => {
        // WhatsApp templates
        const resp = await getWhatsAppTEMP(gymid);
        if (!resp || resp === "false") { setApiError(true); return; }
        const TemplateData   = await resp.json();
        const allTemplates   = TemplateData.messageTemplates || [];

        // ⚠️ DB returns lowercase 'approved'; Meta API returns 'APPROVED' — handle both
        const approvedList   = allTemplates.filter(isApproved);
        setTemplateinfo(approvedList);

        const detectedProvider = TemplateData?.provider || "wati";
        setProvider(detectedProvider);

        // Hydrate templateData if a template was already saved on this form
        const savedTemplateName = location.state?.row?.wtstemplatename;
        if (savedTemplateName) {
            const found = approvedList.find(t => t.elementName === savedTemplateName);
            if (found) setTemplateData(found);
        }

        // App variables
        const res = await getappvariables(gymid);
        if (!res || res === "false") { setApiError(true); return; }
        setAppvariables(await res.json());

        // SMS api config
        const smsResp = await GetAPIsms(gymid);
        if (!smsResp || smsResp === "false") { setApiError(true); return; }
        setSmsapi(await smsResp.json());
    };

    // ── template selection ─────────────────────────────────────────────────
    const handleSelectTemplate = (values) => {
        if (!values) return;
        setSelectedTemplate(values.value);
        const found = templateinfo.find(t => t.elementName === values.value);
        setTemplateData(found || {});
    };

    // ── field helpers ─────────────────────────────────────────────────────────
    const updateForm = (value) => setForm(prev => ({ ...prev, ...value }));

    const handleSelectChange = (index, field, value) => {
        const updated = [...variableMappings];
        updated[index][field] = value;
        setVariableMappings(updated);
    };

    const handleSmsSelectChange = (index, field, value) => {
        const updated = [...smsvableMaping];
        updated[index][field] = value;
        setSmsvarbleMaping(updated);
    };

    const extractSmsVariables = (template) => {
        const regex = /\{#var#\}/g;
        const vars  = [];
        let counter = 1;
        while (regex.exec(template) !== null) vars.push(`variable_${counter++}`);
        return vars;
    };

    // ── save ──────────────────────────────────────────────────────────────────
    const saveFunction = async (e) => {
        e.preventDefault();

        const errors = {};
        variableMappings.forEach((mapping, index) => {
            if (!mapping.applicationvariable)
                errors[index] = `${getVariableLabel(mapping, provider)} is not mapped.`;
        });
        setMappingErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const updates = {
            ...form,
            gymid,
            wtsvariablemaping: variableMappings,
            wtstemplatename:   selectedTemplate,
            smsvariableMaping: smsvableMaping,
            watsapptype:       provider
        };

        const saverep = await updateSMSTemp(updates, gymid);
        if (saverep === "false") { setApiError(true); return; }
        const recinfo = await saverep.json();
        setAlerttxt("Template saved successfully");
        await trackButtonClick("EditMessage", "Update_Template", "staff", {});
        setForm(recinfo);
        setVariableMappings(recinfo.wtsvariablemaping || []);
        setSelectedTemplate(recinfo.wtstemplatename || "");
        hadlecancel();
    };

    // ── SMS preview ────────────────────────────────────────────────────────────
    const replaceTemplateVariables = (template = "", mappings = [], values = {}) => {
        if (!template) return "";
        let result = template;
        let counter = 1;
        result = result.replace(/\{#var#\}/g, () => {
            const mapping = mappings[counter - 1];
            counter++;
            return mapping
                ? values[mapping.applicationvariable] || `{${mapping.applicationvariable}}`
                : `{variable_${counter - 1}}`;
        });
        return result;
    };

    const handlePreviewClick = () => {
        const values = {};
        smsvableMaping.forEach(m => {
            values[m.applicationvariable] = form[m.applicationvariable] || `{${m.applicationvariable}}`;
        });
        const updated = replaceTemplateVariables(form.templete, smsvableMaping, values);
        setPreview(updated);
        setPreviewTemplate(updated);
        setPrevCheck(true);
    };

    const handleInputChange = (e, variable) => {
        const newInputValues = { ...inputValues, [variable]: e.target.value };
        setInputValues(newInputValues);
        let newTemplate = preview;
        Object.keys(newInputValues).forEach(v => {
            const regex = new RegExp(`{${v}}`, 'g');
            newTemplate = newTemplate.replace(regex, newInputValues[v] !== "" ? newInputValues[v] : `{${v}}`);
        });
        setPreviewTemplate(newTemplate);
    };

    // ── misc ──────────────────────────────────────────────────────────────────
    const goBack      = () => navigate("/messageSettings");
    const hadlecancel = () => navigate("/messageSettings");

    const numMapped    = variableMappings.filter(m => m.applicationvariable).length;
    const numSmsMapped = smsvableMaping.filter(m => m.applicationvariable).length;

    const templateDisplayText = getTemplateDisplayText(templateData, provider);
    const headerComp          = templateData?.components?.header;

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            <LeftPane isactive="4" />
            <Header />
            <div className="gym-main gym-main-div rightContent">
                <div className="gym-container gym-cnr" id="showcase">

                    {/* Page header */}
                    <div className="d-flex space-between align-center flex-wrap">
                        <h3 className="gym-large d-flex">
                            <MDBIcon fas icon="arrow-left" className="me-2" onClick={goBack} />
                            <div>{form.smscategory}</div>
                        </h3>
                        <div>
                            <MDBBtn color='secondary' className='me-2' onClick={hadlecancel}>Cancel</MDBBtn>
                            <MDBBtn onClick={saveFunction}>Save</MDBBtn>
                        </div>
                    </div>

                    <div className="my-4 templateEditor">
                        <MDBRow>
                            <MDBCol md={9} lg={9} sm={12}>

                                {/* ── SMS Template card ── */}
                                {plansInfo?.feature?.smsintergration && (
                                    <MDBCard className='p-3 border border-1 border-secondary mt-4 shadow-0'>
                                        <div className="mt-0">
                                            <div className='d-flex align-items-center justify-content-between mb-2'>
                                                <h6>SMS Template</h6>
                                                <div className='d-flex align-items-center'>
                                                    <label className='w-50 d-block'>Template Id: </label>
                                                    <input type='text' className="form-control"
                                                        value={form.templeteid || ""}
                                                        onChange={e => updateForm({ templeteid: e.target.value })} />
                                                </div>
                                                <div className='d-flex align-items-center justify-content-between mb-2 mob-block'>
                                                    <div className='d-flex align-items-center'>
                                                        <label className="switch">
                                                            <input className="form-control" type="checkbox"
                                                                disabled={!form.templete}
                                                                checked={!!form.toggle}
                                                                onChange={() => updateForm({ toggle: !form.toggle })} />
                                                            <span className="slider round"></span>
                                                        </label>
                                                        <p className='m-0 me-3'>Enable/Disable</p>
                                                        {form.smscategory && !(form.smscategory.includes("ADDED") || form.smscategory.includes("SUSPENDED")) && (
                                                            <MDBCheckbox name='flexCheck' checked={!!form.addontoggle}
                                                                onChange={() => updateForm({ addontoggle: !form.addontoggle })}
                                                                id='flexCheckDefault' label='Addon(s)' />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {smsapi.length > 0
                                                ? <textarea className='p-2 w-100 textarea' rows='5'
                                                    placeholder='Please copy & paste the template from the TextLocal platform.'
                                                    name='templete' value={form.templete || ""}
                                                    onChange={e => updateForm({ templete: e.target.value })}
                                                    ref={textAreaRef} />
                                                : <p className='mt-2'>To edit the templates, add the access keys in SMS settings.</p>
                                            }

                                            {smstemvariables.length > 0 && (
                                                <p className='mt-2'><b>Note:</b> N/A will be shown if there is no value for the application variable.</p>
                                            )}
                                            {smstemvariables.length > 0 && (
                                                <div className='py-2 d-flex align-items-center'>
                                                    <div className='ps-0 fw-500 w-30'><b>SMS Variable</b></div>
                                                    <div className='fw-500 w-25'><b>Application Variable</b></div>
                                                </div>
                                            )}
                                            {smsvableMaping.map((mapping, index) => (
                                                <div key={index}>
                                                    <div className='d-flex align-items-center mt-2'>
                                                        <div className='ps-0 fw-bold w-30'>{mapping.smsvariable}</div>
                                                        <div className='ps-0 fw-bold w-30'>
                                                            <select className='form-select w-80'
                                                                value={mapping.applicationvariable}
                                                                onChange={e => handleSmsSelectChange(index, 'applicationvariable', e.target.value)}>
                                                                <option value="" disabled>Select</option>
                                                                {appVariables.map(item => (
                                                                    <option key={item.value} value={item.value}>{item.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    {mappingErrors[index] && <span className='text-danger'>{mappingErrors[index]}</span>}
                                                </div>
                                            ))}
                                            <div className='d-flex justify-content-between mt-2'>
                                                {smsvableMaping.length > 0 && (
                                                    <div className='pt-3 fw-bold'>
                                                        No of variables: {smsvableMaping.length}, mapped: {numSmsMapped}
                                                    </div>
                                                )}
                                                {smsvableMaping.length > 0 && (
                                                    <MDBBtn onClick={handlePreviewClick}>
                                                        <MDBIcon fas icon="eye" className='me-2' />Preview
                                                    </MDBBtn>
                                                )}
                                            </div>
                                        </div>
                                    </MDBCard>
                                )}

                                {/* ── WhatsApp Template card ── */}
                                {plansInfo?.feature?.smsintergration && (
                                    <MDBCard className='p-3 border border-1 border-secondary mt-4 shadow-0'>
                                        <div className="mt-0">

                                            {/* Card heading + enable toggle */}
                                            <div className='d-flex align-items-center justify-content-between mb-2'>
                                                <h6 className='mb-0'>
                                                    WhatsApp Template
                                                    <span className={`ms-2 badge ${provider === 'meta' ? 'bg-primary' : 'bg-success'}`}
                                                        style={{ fontSize: 11 }}>
                                                        {provider === 'meta' ? 'Meta' : 'WATI'}
                                                    </span>
                                                </h6>
                                                <div className='d-flex align-items-center'>
                                                    <label className="switch">
                                                        <input className="form-control" type="checkbox"
                                                            checked={!!form.wtsapptoggle}
                                                            disabled={!templateDisplayText}
                                                            onChange={() => updateForm({ wtsapptoggle: !form.wtsapptoggle })} />
                                                        <span className="slider round"></span>
                                                    </label>
                                                    <p className='m-0 me-3'>Enable/Disable</p>
                                                    {form.smscategory && !(form.smscategory.includes("ADDED") || form.smscategory.includes("SUSPENDED")) && (
                                                        <MDBCheckbox name='flexCheck' checked={!!form.wtsaddontoggle}
                                                            onChange={() => updateForm({ wtsaddontoggle: !form.wtsaddontoggle })}
                                                            id='flexCheckDefault' label="Addon(s)"
                                                            disabled={!form.wtsapptoggle} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Template dropdown */}
                                            <div className='d-flex align-items-center mb-2'>
                                                <h6 className='me-3 mb-0'>Select Template:</h6>
                                                <MDBSelect className='w-50' data={selectData} search
                                                    placeholder='Select here..'
                                                    value={selectedTemplate || ""}
                                                    onChange={handleSelectTemplate} />
                                            </div>

                                            {/* Read-only body textarea */}
                                            {templateinfo.length > 0
                                                ? <MDBTextArea rows={5} value={templateDisplayText} readOnly />
                                                : <p className='mt-2'>To view templates, add access keys in WhatsApp settings.</p>
                                            }

                                            {/* ── IMAGE header info card ── */}
                                            {provider === 'meta' && headerComp?.format === 'IMAGE' && (
                                                <div className='mt-3 p-3 rounded'
                                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                    <div className='d-flex align-items-center gap-2 mb-2'>
                                                        <MDBIcon fas icon="image" style={{ color: '#6366f1' }} />
                                                        <span className='fw-bold' style={{ fontSize: 13 }}>Image Header</span>
                                                        <span className={`badge ms-1 ${headerComp.isDynamic ? 'bg-warning text-dark' : 'bg-secondary'}`}
                                                            style={{ fontSize: 10 }}>
                                                            {headerComp.isDynamic ? '⚡ Dynamic per member' : '📌 Static image'}
                                                        </span>
                                                    </div>

                                                    <div className='d-flex align-items-start gap-3'>
                                                        {/* Try to show the image.
                                                            - If isDynamic and mediaUrl is "{{1}}" — no valid URL, skip img
                                                            - If static — mediaUrl may be a CDN URL or a media handle (not a direct URL)
                                                            Show placeholder in both fallback cases */}
                                                        {headerComp.mediaUrl && !headerComp.isDynamic ? (
                                                            <>
                                                                <img
                                                                    src={headerComp.mediaUrl}
                                                                    alt="Template header"
                                                                    style={{
                                                                        width: 90, height: 90, objectFit: 'cover',
                                                                        borderRadius: 8, border: '1px solid #cbd5e1', flexShrink: 0
                                                                    }}
                                                                    onError={e => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                                {/* Fallback: CDN/handle URL expired or not a direct image URL */}
                                                                <div style={{
                                                                    display: 'none', width: 90, height: 90,
                                                                    background: '#e2e8f0', borderRadius: 8,
                                                                    alignItems: 'center', justifyContent: 'center',
                                                                    flexDirection: 'column', flexShrink: 0,
                                                                    fontSize: 11, color: '#94a3b8', textAlign: 'center'
                                                                }}>
                                                                    <MDBIcon fas icon="image" style={{ fontSize: 20, marginBottom: 4 }} />
                                                                    <span>Preview<br />unavailable</span>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            /* Dynamic image — no single URL to preview */
                                                            <div style={{
                                                                width: 90, height: 90, background: '#ede9fe',
                                                                borderRadius: 8, display: 'flex', alignItems: 'center',
                                                                justifyContent: 'center', flexDirection: 'column',
                                                                flexShrink: 0, fontSize: 11, color: '#7c3aed'
                                                            }}>
                                                                <MDBIcon fas icon="qrcode" style={{ fontSize: 22, marginBottom: 4 }} />
                                                                <span style={{ textAlign: 'center' }}>Per<br />member</span>
                                                            </div>
                                                        )}

                                                        <div style={{ fontSize: 12, color: '#64748b' }}>
                                                            {headerComp.isDynamic ? (
                                                                <>
                                                                    <p className='mb-1 fw-bold' style={{ color: '#92400e' }}>
                                                                        ⚡ This image is unique per member (e.g. QR code)
                                                                    </p>
                                                                    <p className='mb-1'>
                                                                        URL template: <code style={{ fontSize: 11, background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>
                                                                            {headerComp.mediaUrl || '{{1}}'}
                                                                        </code>
                                                                    </p>
                                                                    <p className='mb-0'>
                                                                        Map <strong>Header Image URL</strong> below to the application field
                                                                        that holds each member's image URL.
                                                                    </p>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <p className='mb-1 fw-bold' style={{ color: '#475569' }}>
                                                                        📌 Same image sent to all recipients
                                                                    </p>
                                                                    <p className='mb-0'>
                                                                        No mapping needed — this image is fixed on the template.
                                                                    </p>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Variable mapping table */}
                                            {variableMappings.length > 0 && (
                                                <p className='mt-3'>
                                                    <b>Note:</b> N/A will be shown if there is no value for the application variable.
                                                </p>
                                            )}
                                            {variableMappings.length > 0 && (
                                                <div className='py-2 d-flex align-items-center'>
                                                    <div className='ps-0 fw-500 w-30'><b>WhatsApp Variable</b></div>
                                                    <div className='fw-500 w-25'><b>Application Variable</b></div>
                                                </div>
                                            )}

                                            {templateDisplayText && variableMappings.map((mapping, index) => (
                                                <div key={index}>
                                                    <div className='d-flex align-items-center mt-2'>
                                                        <div className='ps-0 fw-bold w-30 d-flex align-items-center gap-2'>
                                                            <span>{getVariableLabel(mapping, provider)}</span>
                                                            {provider === 'meta' && (
                                                                <span className={`badge ${
                                                                    mapping.type === 'image'
                                                                        ? 'bg-purple text-white'
                                                                        : mapping.location === 'header'
                                                                            ? 'bg-warning text-dark'
                                                                            : 'bg-info text-dark'
                                                                }`} style={{ fontSize: 10, background: mapping.type === 'image' ? '#7c3aed' : undefined }}>
                                                                    {mapping.type === 'image' ? '🖼 image' : mapping.location}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className='ps-0 fw-bold w-30'>
                                                            <select className='form-select w-80'
                                                                value={mapping.applicationvariable}
                                                                onChange={e => handleSelectChange(index, 'applicationvariable', e.target.value)}>
                                                                <option value="" disabled>Select</option>
                                                                {appVariables.map((appVar, i) => (
                                                                    <option key={i} value={appVar.value}>{appVar.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    {mappingErrors[index] && (
                                                        <span className='text-danger'>{mappingErrors[index]}</span>
                                                    )}
                                                </div>
                                            ))}

                                            {templateDisplayText && (
                                                <div className='pt-3 fw-bold'>
                                                    No of variables: {variableMappings.length}, mapped: {numMapped}
                                                </div>
                                            )}
                                        </div>
                                    </MDBCard>
                                )}

                                {/* ── Email Template card ── */}
                                <MDBCard className='p-3 border border-1 border-secondary mt-4 shadow-0'>
                                    <div className="mt-0">
                                        <div className='d-flex align-items-center justify-content-between mb-2'>
                                            <h6>Email {form.smscategory === "MEMBER_QR_Code_ALERT" ? "" : "Template"}</h6>
                                            <div className='d-flex align-items-center'>
                                                <label className="switch">
                                                    <input className="form-control" type="checkbox"
                                                        checked={!!form.emailtoggle}
                                                        onChange={() => updateForm({ emailtoggle: !form.emailtoggle })} />
                                                    <span className="slider round"></span>
                                                </label>
                                                <p className='m-0 me-3'>Enable/Disable</p>
                                            </div>
                                        </div>
                                        {form.smscategory === "MEMBERSHIP_PAYMENT_ALERT"
                                            ? <EmailTempletes />
                                            : form.smscategory === "MEMBER_QR_Code_ALERT"
                                                ? <></>
                                                : <div>Not applicable.</div>
                                        }
                                    </div>
                                </MDBCard>

                            </MDBCol>
                        </MDBRow>
                    </div>
                </div>
            </div>

            {/* Alert */}
            <MDBAlert color="success" autohide position='top-center' delay={5000} appendToBody
                open={Alerttxt !== ""} onClose={() => setAlerttxt("")}>
                {Alerttxt}
            </MDBAlert>

            {/* SMS Preview modal */}
            <MDBModal staticBackdrop tabIndex='-1' open={prevCheck} onClose={() => setPrevCheck(false)}>
                <MDBModalDialog size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle><h6 className="m-0">Preview Template</h6></MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={() => setPrevCheck(false)} />
                        </MDBModalHeader>
                        <MDBModalBody>
                            <div className='d-flex mb-2'>
                                <div className='d-flex align-items-center border w-100'>
                                    <p className='p-3 mb-0'
                                        dangerouslySetInnerHTML={{ __html: previewTemplate.replace(/%n/g, '<br />') }} />
                                </div>
                            </div>
                            <h6 className='my-3'>Custom Variables</h6>
                            {smsvableMaping.map((mapping, index) => (
                                <div className='d-block d-md-flex align-items-center mb-2' key={index}>
                                    <p className='mb-0 me-2 w-20'><strong>{mapping.applicationvariable}:</strong></p>
                                    <input type='text' className='form-control' placeholder='Enter value'
                                        value={inputValues[mapping.applicationvariable] || ''}
                                        onChange={e => handleInputChange(e, mapping.applicationvariable)} />
                                </div>
                            ))}
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </React.Fragment>
    );
};

export default EditMessage;